import React, { useState, useEffect, useCallback } from 'react';
import SpellCheckerSidebar from '../SpellChecker/SpellCheckerSidebar';
import { SpellCheckMatch } from '@/services/spellCheckerService';
import { getSpellingErrors, getGrammarErrors } from '@/utils/spellCheckerUtils';

type SectionKey = 'punctuation' | 'spelling' | 'grammar' | 'suggestion';

interface AnalysisResultsProps {
  analysis: any;
  onWordHighlight?: (offset: number, length: number) => void;
  onSuggestionReplace?: (match: SpellCheckMatch, suggestion: string) => void;
  spellCheckResults?: SpellCheckMatch[];
  isChecking?: boolean;
  onIgnoreError?: (match: SpellCheckMatch) => void;
}

const AnalysisResults: React.FC<AnalysisResultsProps> = ({
  analysis,
  onWordHighlight,
  onSuggestionReplace,
  spellCheckResults = [],
  isChecking = false,
  onIgnoreError,
}) => {
  if (!analysis) return null;



  const overviewImprovements = analysis.improvements.filter((improvement: any) => improvement.type === 'OVERVIEW');
  const suggestionImprovements = analysis.improvements.filter((improvement: any) => improvement.type === 'SUGGESTIONS');


  const [sectionOpen, setSectionOpen] = useState<Record<SectionKey, boolean>>({
    punctuation: true,
    spelling: false,
    grammar: false,
    suggestion: false
  });

  const handleSuggestionClick = (
    match: SpellCheckMatch,
    suggestion: string
  ) => {
    if (onSuggestionReplace) {
      onSuggestionReplace(match, suggestion);
    }
  };

  const handleWordClick = (match: SpellCheckMatch) => {
    if (onWordHighlight) {
      onWordHighlight(match.offset, match.length);
    }
  };

  const spellingErrors = getSpellingErrors(spellCheckResults);
  const grammarErrors = getGrammarErrors(spellCheckResults);


  const counter: Record<SectionKey, string> = {
    spelling: `(${spellingErrors.length})`,
    grammar: `(${grammarErrors.length})`,
    suggestion: ``,
    punctuation: ``
  }

  const ErrorSection = ({
    title,
    content,
    icon,
    showSpellChecker = false,
  }: {
    title: string;
    content: string;
    icon: SectionKey;
    showSpellChecker?: boolean;
  }) => {



    const renderIcon = () => {
      switch (icon) {
        case 'spelling':
          return (
            <svg
              className="w-5 h-5 text-red-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
          );
        case 'grammar':
          return (
            <svg
              className="w-5 h-5 text-yellow-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          );
        case 'punctuation':
          return (
            <svg
              className="w-5 h-5 text-green-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
          );
        case 'suggestion':
          return (
            <svg
              className="w-5 h-5 text-blue-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.476.859h4.002z" />
            </svg>
          );
        default:
          return null;
      }
    };

    const parseContent = (content: any) => {
      if (title === 'Improvement Suggestions') {
       
        return content.map((item: any) => item.content);
      }
      return content.split('\n').filter((item: string) => item.trim() !== '');
    };

    const renderContentFeedback = () => (
      <p className="leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{parseContent(content)}</p>
    );

    const renderImprovementSuggestions = () => (
      <ul className="space-y-2">
        {parseContent(content).map((suggestion: string, index: number) => (
          <li key={index} className="flex items-start">
           <span className="text-blue-500 mr-2 mt-1">{index+1}.</span>
            <span className="leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{suggestion}</span>
          </li>
        ))}
      </ul>
    );

    const renderSpellChecker = () => (
      <SpellCheckerSidebar
        spellCheckResults={
          title.includes('Spelling')
            ? spellingErrors
            : grammarErrors
        }
        isChecking={isChecking}
        onSuggestionClick={handleSuggestionClick}
        onIgnoreError={onIgnoreError || (() => {})}
        onWordClick={handleWordClick}
      />
    );

    const renderContent = () => {
      if (title === 'Content Feedback') {
        return renderContentFeedback();
      }
      
      if (title === 'Improvement Suggestions') {
        return renderImprovementSuggestions();
      }
      
      if (showSpellChecker) {
        return renderSpellChecker();
      }
      
      return null;
    };

    return (
      <div className="error-section border rounded-lg mb-4" style={{ background: 'var(--surface-primary)', borderColor: 'var(--border-primary)' }}>
        <div
          className="error-header p-4 cursor-pointer hover:bg-[var(--surface-hover)] transition-colors flex items-center justify-between"
          onClick={() => {
            setSectionOpen({
              ...sectionOpen,
              [icon as SectionKey]: !sectionOpen[icon as SectionKey]
            })
          }}
        >
          <div className="flex items-center space-x-3">
            {renderIcon()}
            <span className="error-title font-medium" style={{ color: 'var(--text-primary)' }}>
              {title === 'Content Feedback' ? title : `${counter[icon as SectionKey]} ${title}`}
            </span>
          </div>
          <svg
            className={`w-5 h-5 transition-transform ${sectionOpen[icon as SectionKey] ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
        
        {sectionOpen[icon as SectionKey] && (
          <div className="px-4 pb-4">
            {renderContent()}
          </div>
        )}    
      </div>
    );
  };

  return (
    <div className="analysis-results p-6">
      <h3 className="text-2xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>

        Analysis Results
      </h3>
    
      <ErrorSection
        title="Content Feedback"
        content={overviewImprovements[0].content}
        icon="punctuation"
      />

      <ErrorSection
        title="Spelling Error(s)"
        content={analysis.spelling_errors}
        icon="spelling"
        showSpellChecker={true}
      />

      <ErrorSection
        title="Grammar & Punctuation Error(s)"
        content={analysis.grammar_errors}
        icon="grammar"
        showSpellChecker={true}
      />

      <ErrorSection
        title="Improvement Suggestions"
        content={suggestionImprovements}
        icon="suggestion"
      />
    </div>
  );
};

export default AnalysisResults;
